import { supabase } from './supabase'

/**
 * The audit log — #/admin/audit (196, ACC-8; David, 13 Oct).
 *
 * A reader over public.event, the append-only record every write path has
 * fed since migration 003. Nothing here writes, and nothing here is cached:
 * the log is the thing an administrator checks when something is disputed, so
 * every filter change asks the database again.
 *
 * ── THE SERVER CLASSIFIES, THE PAGE DRAWS ───────────────────────────────────
 *
 * Area, kind and the plain label come from public.audit_action through
 * audit_log(); names come from person at read time (rule 5 — the payload
 * holds ids, never names). The page never decides what an action "is", so a
 * new action catalogued in a migration reads correctly without a web change,
 * and an uncatalogued one still shows (area "other") rather than vanishing.
 *
 * ── GUARDED TWICE ───────────────────────────────────────────────────────────
 *
 * The nav item renders only for audit.read (tenant_admin), and all three RPCs
 * refuse without it. hr_admin holds admin.roles and is refused on purpose: an
 * audit trail is not readable by every administrator it audits.
 */

export type AuditKind = 'admin' | 'staff' | 'system'
export type AuditPersonAs = 'either' | 'actor' | 'subject'

export const KIND_LABEL: Record<AuditKind, string> = {
  admin: 'Admin action',
  staff: 'Staff activity',
  system: 'Automatic',
}

/** What each kind means, under the filter. Said once, here. */
export const KIND_HINT: Record<AuditKind, string> = {
  admin: 'Done to someone else, or to the platform’s settings',
  staff: 'People signing in, and acting on their own posts, questions, stars and settings',
  system: 'The HR sync, translation, checks and other jobs',
}

export const AREA_LABEL: Record<string, string> = {
  access: 'Roles and access',
  people: 'People records',
  content: 'Posts and notices',
  moderation: 'Moderation',
  wiki: 'Wiki One and Ask One',
  support: 'Support',
  stars: 'Star One',
  shop: 'Shop and stock',
  photos: 'Photos and contests',
  sync: 'HR sync',
  language: 'Language and translation',
  platform: 'Platform settings',
  other: 'Other',
}

export function areaLabel(area: string): string {
  return AREA_LABEL[area] ?? area
}

export type AuditRow = {
  id: number
  occurredAt: string
  action: string
  label: string
  area: string
  kind: AuditKind
  actorId: string | null
  actorName: string | null
  subjectType: string
  subjectId: string | null
  subjectLabel: string | null
  payload: Record<string, unknown>
  /** person id → display name, for every payload key ending in person_id. */
  people: Record<string, string>
}

export type AuditFilters = {
  kinds: string[]
  areas: string[]
  actions: string[]
  personId: string | null
  personAs: AuditPersonAs
  /** yyyy-mm-dd in the viewer's own calendar; inclusive at both ends. */
  fromDay: string
  toDay: string
  search: string
}

export const EMPTY_FILTERS: AuditFilters = {
  kinds: [],
  areas: [],
  actions: [],
  personId: null,
  personAs: 'either',
  fromDay: '',
  toDay: '',
  search: '',
}

/** The page opens on admin actions (David, 13 Oct); staff activity is one filter away. */
export const DEFAULT_FILTERS: AuditFilters = { ...EMPTY_FILTERS, kinds: ['admin'] }

export const PAGE_SIZE = 100

/** Local midnight of a yyyy-mm-dd, as an instant. Rule 9: the database holds UTC. */
export function dayStartIso(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d).toISOString()
}

/** The midnight AFTER a yyyy-mm-dd — so "to 13 Oct" includes all of the 13th. */
export function dayEndIso(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d + 1).toISOString()
}

type RawRow = {
  id: number
  occurred_at: string
  action: string
  label: string
  area: string
  kind: AuditKind
  actor_person_id: string | null
  actor_name: string | null
  subject_type: string
  subject_id: string | null
  subject_label: string | null
  payload: Record<string, unknown> | null
  people: Record<string, string> | null
}

/** One page, newest first. `before` is the oldest id already on screen. */
export async function loadAuditPage(f: AuditFilters, before: number | null = null): Promise<AuditRow[]> {
  const { data, error } = await supabase.rpc('audit_log', {
    p_from: dayStartIso(f.fromDay),
    p_to: dayEndIso(f.toDay),
    p_person: f.personId,
    p_person_as: f.personAs,
    p_kinds: f.kinds.length ? f.kinds : null,
    p_areas: f.areas.length ? f.areas : null,
    p_actions: f.actions.length ? f.actions : null,
    p_search: f.search.trim() || null,
    p_before: before,
    p_limit: PAGE_SIZE,
  })
  if (error) throw error
  return ((data ?? []) as RawRow[]).map((r) => ({
    id: Number(r.id),
    occurredAt: r.occurred_at,
    action: r.action,
    label: r.label,
    area: r.area,
    kind: r.kind,
    actorId: r.actor_person_id,
    actorName: r.actor_name,
    subjectType: r.subject_type,
    subjectId: r.subject_id,
    subjectLabel: r.subject_label,
    payload: r.payload ?? {},
    people: r.people ?? {},
  }))
}

export type AuditAction = {
  action: string
  label: string
  area: string
  kind: string
  events: number
  lastAt: string | null
}

/** Every catalogued action plus any the log holds that is not, with counts. */
export async function loadAuditActions(): Promise<AuditAction[]> {
  const { data, error } = await supabase.rpc('audit_log_actions')
  if (error) throw error
  return (
    (data ?? []) as { action: string; label: string; area: string; kind: string; events: number; last_at: string | null }[]
  ).map((r) => ({
    action: r.action,
    label: r.label,
    area: r.area,
    kind: r.kind,
    events: Number(r.events),
    lastAt: r.last_at,
  }))
}

export type AuditPerson = {
  personId: string
  displayName: string
  isService: boolean
  isActive: boolean
  acts: number
}

/** Members past and present, and the service principals that act here. */
export async function searchAuditPeople(q: string): Promise<AuditPerson[]> {
  const { data, error } = await supabase.rpc('audit_log_people', { p_search: q.trim() || null, p_limit: 20 })
  if (error) throw error
  return (
    (data ?? []) as { person_id: string; display_name: string; is_service: boolean; is_active: boolean; acts: number }[]
  ).map((r) => ({
    personId: r.person_id,
    displayName: r.display_name,
    isService: r.is_service,
    isActive: r.is_active,
    acts: Number(r.acts),
  }))
}

/** "first_published_at" → "First published at". */
export function humanKey(key: string): string {
  const words = key.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/** A payload value in words. A person id becomes their name when the server named it. */
export function showValue(v: unknown, people: Record<string, string>): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'number') return v.toLocaleString()
  if (typeof v === 'string') {
    if (v === '') return '(empty)'
    if (UUID.test(v) && people[v]) return people[v]
    if (ISO.test(v)) {
      const d = new Date(v)
      if (!Number.isNaN(d.getTime())) return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    }
    return v
  }
  if (Array.isArray(v)) return v.length === 0 ? '(none)' : v.map((x) => showValue(x, people)).join(', ')
  return JSON.stringify(v)
}

export type AuditChange = { field: string; was: unknown; now: unknown }

/**
 * What changed, when the writer recorded both sides. Three shapes are in use
 * (checked against dev, 13 Oct): `before`/`after` objects (stars.rule_changed),
 * `x_was`/`x_now` pairs (shop_item.edited), and a bare `was`/`now` or
 * `from`/`to` (catalogue_set, locale corrections). Anything else is shown as
 * plain facts — most writers record what was set, not what it replaced.
 */
export function describeChanges(payload: Record<string, unknown>): AuditChange[] {
  const out: AuditChange[] = []
  const before = payload.before
  const after = payload.after
  if (before && after && typeof before === 'object' && typeof after === 'object') {
    const b = before as Record<string, unknown>
    const a = after as Record<string, unknown>
    for (const k of new Set([...Object.keys(b), ...Object.keys(a)])) {
      if (JSON.stringify(b[k]) !== JSON.stringify(a[k])) out.push({ field: humanKey(k), was: b[k], now: a[k] })
    }
  }
  for (const k of Object.keys(payload)) {
    const m = /^(.*)_was$/.exec(k)
    if (m && `${m[1]}_now` in payload) out.push({ field: humanKey(m[1]), was: payload[k], now: payload[`${m[1]}_now`] })
  }
  if ('was' in payload && 'now' in payload) out.push({ field: 'Value', was: payload.was, now: payload.now })
  if ('from' in payload && 'to' in payload) out.push({ field: 'Value', was: payload.from, now: payload.to })
  return out
}

/** The payload keys describeChanges already showed, so the facts do not repeat them. */
export function changeKeys(payload: Record<string, unknown>): Set<string> {
  const keys = new Set<string>()
  if (payload.before && payload.after) {
    keys.add('before')
    keys.add('after')
  }
  for (const k of Object.keys(payload)) {
    const m = /^(.*)_was$/.exec(k)
    if (m && `${m[1]}_now` in payload) {
      keys.add(k)
      keys.add(`${m[1]}_now`)
    }
  }
  if ('was' in payload && 'now' in payload) {
    keys.add('was')
    keys.add('now')
  }
  if ('from' in payload && 'to' in payload) {
    keys.add('from')
    keys.add('to')
  }
  return keys
}

/** "13 Oct, 14:02" in the viewer's own zone; the year only when it is not this one. */
export function shortWhen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  const sameYear = d.getFullYear() === now.getFullYear()
  const day = d.toLocaleDateString(undefined, sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return `${day}, ${time}`
}

/** The full instant with its zone, for the tray. */
export function fullWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  })
}

/** Who did it, in words. No actor is the platform itself; an erased person has an id and no name. */
export function actorText(r: Pick<AuditRow, 'actorId' | 'actorName'>): string {
  if (!r.actorId) return 'The platform'
  return r.actorName ?? 'A removed person'
}

/**
 * What it was done to: the server's label, or the subject's type when there is
 * none (a type app.audit_subject does not name, or a row since removed).
 */
export function subjectText(r: Pick<AuditRow, 'subjectType' | 'subjectLabel'>): string {
  return r.subjectLabel ?? humanKey(r.subjectType)
}
